import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { SEOHead } from '@/components/seo-head'
import { Calendar, User, ArrowRight, TrendingUp, FileText, Lightbulb, Target } from 'lucide-react'

export function Blog() {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "SourcedIn RFP Marketplace Blog",
    "description": "Expert insights on RFP best practices, procurement trends, and B2B marketplace strategies",
    "blogPost": [
      {
        "@type": "BlogPosting",
        "headline": "10 Best Practices for Writing Winning RFP Responses",
        "datePublished": "2024-01-15",
        "author": {
          "@type": "Person",
          "name": "Sarah Chen"
        }
      }
    ]
  }

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="RFP Marketplace Blog - Expert Insights & Best Practices | SourcedIn"
        description="Expert insights on RFP best practices, procurement strategies, vendor selection, and B2B marketplace trends. Learn from industry leaders and grow your business."
        keywords="RFP blog, procurement insights, vendor selection tips, B2B marketplace strategies, request for proposal best practices, business procurement"
        canonical="https://sourcedin.ai/blog"
        structuredData={structuredData}
      />

      {/* Hero Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            RFP Marketplace 
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600"> Insights</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Expert insights, best practices, and actionable strategies to help you master 
            the art of procurement and vendor relationships.
          </p>
        </div>
      </section>

      {/* Featured Article */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="bg-blue-100 text-blue-600 px-3 py-1 rounded-full text-sm font-medium">Featured Article</span>
          </div>
          
          <Card className="border-0 shadow-2xl overflow-hidden">
            <div className="grid md:grid-cols-2">
              <div className="bg-gradient-to-br from-blue-600 to-purple-600 p-12 text-white">
                <div className="h-full flex flex-col justify-center">
                  <h2 className="text-3xl md:text-4xl font-bold mb-6 leading-tight">
                    The Complete Guide to RFP Success in 2024
                  </h2>
                  <p className="text-xl opacity-90 mb-8 leading-relaxed">
                    Everything you need to know about creating, managing, and winning RFPs 
                    in today's competitive marketplace.
                  </p>
                  <div className="flex items-center space-x-4 text-sm opacity-80">
                    <div className="flex items-center space-x-2">
                      <User className="h-4 w-4" />
                      <span>Sarah Chen, Procurement Expert</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Calendar className="h-4 w-4" />
                      <span>January 15, 2024</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="p-12">
                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">What You'll Learn:</h3>
                    <ul className="space-y-2 text-gray-600">
                      <li className="flex items-start space-x-3">
                        <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                        <span>How to write RFPs that attract top-quality vendors</span>
                      </li>
                      <li className="flex items-start space-x-3">
                        <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                        <span>Evaluation criteria that lead to better outcomes</span>
                      </li>
                      <li className="flex items-start space-x-3">
                        <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                        <span>Common mistakes that waste time and money</span>
                      </li>
                      <li className="flex items-start space-x-3">
                        <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                        <span>Technology tools that streamline the process</span>
                      </li>
                    </ul>
                  </div>
                  <Button className="w-full text-lg py-3">
                    Read Full Article <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Categories */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Explore by Category
            </h2>
            <p className="text-xl text-gray-600">
              Find exactly what you're looking for
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            <Card className="text-center border-0 shadow-lg hover:shadow-xl transition-shadow cursor-pointer group">
              <CardHeader className="pb-4">
                <div className="w-16 h-16 bg-blue-100 group-hover:bg-blue-200 transition-colors rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileText className="h-8 w-8 text-blue-600" />
                </div>
                <CardTitle className="text-xl">RFP Best Practices</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  Expert tips for creating and managing successful RFPs
                </CardDescription>
                <div className="mt-4 text-sm text-blue-600 font-medium">
                  12 Articles
                </div>
              </CardContent>
            </Card>

            <Card className="text-center border-0 shadow-lg hover:shadow-xl transition-shadow cursor-pointer group">
              <CardHeader className="pb-4">
                <div className="w-16 h-16 bg-green-100 group-hover:bg-green-200 transition-colors rounded-full flex items-center justify-center mx-auto mb-4">
                  <Target className="h-8 w-8 text-green-600" />
                </div>
                <CardTitle className="text-xl">Vendor Selection</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  How to find, evaluate, and choose the right vendors
                </CardDescription>
                <div className="mt-4 text-sm text-green-600 font-medium">
                  8 Articles
                </div>
              </CardContent>
            </Card>

            <Card className="text-center border-0 shadow-lg hover:shadow-xl transition-shadow cursor-pointer group">
              <CardHeader className="pb-4">
                <div className="w-16 h-16 bg-purple-100 group-hover:bg-purple-200 transition-colors rounded-full flex items-center justify-center mx-auto mb-4">
                  <TrendingUp className="h-8 w-8 text-purple-600" />
                </div>
                <CardTitle className="text-xl">Market Trends</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  Latest trends and insights in B2B procurement
                </CardDescription>
                <div className="mt-4 text-sm text-purple-600 font-medium">
                  6 Articles
                </div>
              </CardContent>
            </Card>

            <Card className="text-center border-0 shadow-lg hover:shadow-xl transition-shadow cursor-pointer group">
              <CardHeader className="pb-4">
                <div className="w-16 h-16 bg-orange-100 group-hover:bg-orange-200 transition-colors rounded-full flex items-center justify-center mx-auto mb-4">
                  <Lightbulb className="h-8 w-8 text-orange-600" />
                </div>
                <CardTitle className="text-xl">Success Tips</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  Proven strategies for marketplace success
                </CardDescription>
                <div className="mt-4 text-sm text-orange-600 font-medium">
                  10 Articles
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Recent Articles */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Latest Articles
            </h2>
            <p className="text-xl text-gray-600">
              Stay up-to-date with the latest insights and strategies
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader className="pb-4">
                <div className="w-full h-48 bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg mb-4 flex items-center justify-center">
                  <FileText className="h-16 w-16 text-blue-600" />
                </div>
                <CardTitle className="text-xl leading-tight">
                  10 Best Practices for Writing Winning RFP Responses
                </CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base mb-4 leading-relaxed">
                  Learn how to craft compelling RFP responses that stand out from the competition 
                  and win more business.
                </CardDescription>
                <div className="flex items-center justify-between text-sm text-gray-500">
                  <div className="flex items-center space-x-2">
                    <User className="h-4 w-4" />
                    <span>Mike Johnson</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Calendar className="h-4 w-4" />
                    <span>Jan 12, 2024</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader className="pb-4">
                <div className="w-full h-48 bg-gradient-to-br from-green-100 to-green-200 rounded-lg mb-4 flex items-center justify-center">
                  <Target className="h-16 w-16 text-green-600" />
                </div>
                <CardTitle className="text-xl leading-tight">
                  How AI is Transforming Vendor Matching in 2024
                </CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base mb-4 leading-relaxed">
                  Discover how artificial intelligence is revolutionizing the way businesses 
                  find and connect with the right vendors.
                </CardDescription>
                <div className="flex items-center justify-between text-sm text-gray-500">
                  <div className="flex items-center space-x-2">
                    <User className="h-4 w-4" />
                    <span>Sarah Chen</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Calendar className="h-4 w-4" />
                    <span>Jan 10, 2024</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader className="pb-4">
                <div className="w-full h-48 bg-gradient-to-br from-purple-100 to-purple-200 rounded-lg mb-4 flex items-center justify-center">
                  <TrendingUp className="h-16 w-16 text-purple-600" />
                </div>
                <CardTitle className="text-xl leading-tight">
                  The Future of B2B Marketplaces: 5 Key Trends
                </CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base mb-4 leading-relaxed">
                  Explore the emerging trends that are shaping the future of B2B marketplaces 
                  and procurement technology.
                </CardDescription>
                <div className="flex items-center justify-between text-sm text-gray-500">
                  <div className="flex items-center space-x-2">
                    <User className="h-4 w-4" />
                    <span>David Lee</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Calendar className="h-4 w-4" />
                    <span>Jan 8, 2024</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mt-12">
            <Button variant="outline" size="lg" className="text-lg px-8 py-4">
              View All Articles <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* Newsletter Signup */}
      <section className="py-20 px-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Stay Informed
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Get the latest RFP insights and marketplace trends delivered to your inbox weekly.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 px-4 py-3 rounded-lg text-gray-900 text-lg"
            />
            <Button size="lg" variant="secondary" className="text-lg px-8 py-3">
              Subscribe
            </Button>
          </div>
          <p className="text-sm opacity-70 mt-4">
            No spam. Unsubscribe at any time.
          </p>
        </div>
      </section>
    </div>
  )
}
